/**
 * SalesTrendScreen — net sales over time (day / week / month) with the previous
 * period and same-period-last-year overlaid. Answers "are we growing?", which
 * the hourly histogram on the Sales report cannot.
 *
 * Computed entirely from local WatermelonDB — no network required.
 */

import { useCallback, useEffect, useState } from 'react';
import { Download, RefreshCw, ArrowUp, ArrowDown } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import {
  presetRange,
  toDateInputValue,
  startOfDay,
  lastIncludedInstant,
} from '@/utils/dateRange';
import { Button } from '@/components/ui/Button';
import { pdf, file } from '@/platform';
import { toCsv } from '@/utils/csv';
import { loadSalesTrend } from '@/reports/salesTrend/salesTrendDataSource';
import { buildSalesTrend, type SalesTrend, type Granularity, type TrendBucket } from '@/reports/salesTrend/salesTrend';

type Preset = 'week' | 'month' | 'custom';

// ─── Component ───────────────────────────────────────────────────────────────

export function SalesTrendScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const [preset, setPreset] = useState<Preset>('month');
  const [granularity, setGranularity] = useState<Granularity>('day');
  const [customFrom, setCustomFrom] = useState(toDateInputValue(startOfDay(new Date())));
  const [customTo, setCustomTo]     = useState(toDateInputValue(new Date()));
  const [data, setData]             = useState<SalesTrend | null>(null);
  const [loading, setLoading]       = useState(true);
  const [exporting, setExporting]   = useState(false);

  const range = useCallback(
    (): [Date, Date] => presetRange(preset, { from: customFrom, to: customTo }),
    [preset, customFrom, customTo],
  );

  const load = useCallback(async () => {
    if (!activeStoreId) return;
    setLoading(true);
    try {
      const [from, to] = range();
      const source = await loadSalesTrend(activeStoreId, from, to, granularity);
      setData(buildSalesTrend(source));
    } finally {
      setLoading(false);
    }
  }, [activeStoreId, range, granularity]);

  useEffect(() => { void load(); }, [load]);

  const rangeStamp = (): string => {
    const [from, to] = range();
    return `${toDateInputValue(from)}_${toDateInputValue(lastIncludedInstant(to))}`;
  };

  const handleExportCsv = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const csv = toCsv(data.buckets, [
        { header: 'Period', value: (b) => formatBucket(b.start, data.granularity) },
        { header: 'Current', value: (b) => b.current },
        { header: 'Previous', value: (b) => b.previous },
        { header: 'Last year', value: (b) => b.lastYear },
      ]);
      await file.saveText({
        content: csv,
        defaultFileName: `SalesTrend_${data.granularity}_${rangeStamp()}.csv`,
        filters: [{ name: 'CSV', extensions: ['csv'] }],
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const [from, to] = range();
      await pdf.saveFromHtml({
        html: buildTrendHtml(data, from, to),
        defaultFileName: `SalesTrend_${rangeStamp()}.pdf`,
      });
    } finally {
      setExporting(false);
    }
  };

  const maxVal = data
    ? Math.max(1, ...data.buckets.flatMap((b) => [b.current, b.previous, b.lastYear]))
    : 1;

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {(['week', 'month', 'custom'] as Preset[]).map((p) => (
          <button key={p} type="button" onClick={() => setPreset(p)}
            className={cn('rounded-md px-3 py-1.5 text-body transition-colors',
              preset === p ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle')}>
            {p === 'week' ? 'Last 7 days' : p === 'month' ? 'This month' : 'Custom'}
          </button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} max={customTo}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
            <span className="text-fg-tertiary">–</span>
            <input type="date" value={customTo} min={customFrom}
              onChange={(e) => setCustomTo(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
          </div>
        )}
        <div className="mx-1 h-5 w-px bg-stroke-hairline/20" />
        {(['day', 'week', 'month'] as Granularity[]).map((g) => (
          <button key={g} type="button" onClick={() => setGranularity(g)}
            className={cn('rounded-md px-2.5 py-1.5 text-label uppercase transition-colors',
              granularity === g ? 'bg-accent/10 text-accent-ink' : 'text-fg-tertiary hover:bg-bg-subtle')}>
            {g}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </Button>
          <Button variant="secondary" onClick={() => void handleExportCsv()} disabled={!data || exporting || data.buckets.length === 0}>
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="secondary" onClick={() => void handleExportPdf()} disabled={!data || exporting || data.buckets.length === 0}>
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-body text-fg-tertiary">Loading…</div>
      ) : !data ? null : data.buckets.length === 0 ? (
        <div className="mt-16 text-center text-body text-fg-tertiary">No sales in this period.</div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <KPI label="This period" value={formatCurrency(data.totals.current)} accent />
            <KPI label="vs previous period" value={formatCurrency(data.totals.previous)}
              delta={data.hasPrevious ? data.deltaPrevPct : null} />
            <KPI label="vs same period last year" value={formatCurrency(data.totals.lastYear)}
              delta={data.hasLastYear ? data.deltaLastYearPct : null} />
          </div>

          {/* Chart */}
          <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
            <div className="mb-3 flex items-center gap-4">
              <Legend color="bg-accent" label="This period" />
              {data.hasPrevious && <Legend color="bg-accent/40" label="Previous" />}
              {data.hasLastYear && <Legend color="bg-fg-tertiary/40" label="Last year" />}
            </div>
            <div className="overflow-x-auto">
              <div className="flex h-40 items-end gap-2" style={{ minWidth: `${data.buckets.length * 34}px` }}>
                {data.buckets.map((b) => (
                  <div key={b.index} className="flex flex-1 flex-col items-center gap-1" title={tooltip(b, data)}>
                    <div className="flex h-32 w-full items-end justify-center gap-[2px]">
                      <Bar value={b.current} max={maxVal} className="bg-accent" />
                      {data.hasPrevious && <Bar value={b.previous} max={maxVal} className="bg-accent/40" />}
                      {data.hasLastYear && <Bar value={b.lastYear} max={maxVal} className="bg-fg-tertiary/40" />}
                    </div>
                    <span className="whitespace-nowrap text-label text-fg-tertiary">{formatBucket(b.start, data.granularity)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Table */}
          <Card title="By period">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-body">
                <thead>
                  <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                    <th className="pb-1 text-left font-normal">Period</th>
                    <th className="pb-1 text-right font-normal">This period</th>
                    {data.hasPrevious && <th className="pb-1 text-right font-normal">Previous</th>}
                    {data.hasLastYear && <th className="pb-1 text-right font-normal">Last year</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.buckets.map((b) => (
                    <tr key={b.index} className="border-b border-stroke-hairline/10">
                      <td className="py-1.5 text-fg-primary">{formatBucket(b.start, data.granularity)}</td>
                      <td className="py-1.5 text-right tabular-nums text-fg-primary">{formatCurrency(b.current)}</td>
                      {data.hasPrevious && <td className="py-1.5 text-right tabular-nums text-fg-tertiary">{formatCurrency(b.previous)}</td>}
                      {data.hasLastYear && <td className="py-1.5 text-right tabular-nums text-fg-tertiary">{formatCurrency(b.lastYear)}</td>}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-stroke-hairline/20 font-medium">
                    <td className="pt-2 text-fg-primary">Total</td>
                    <td className="pt-2 text-right tabular-nums text-accent-ink">{formatCurrency(data.totals.current)}</td>
                    {data.hasPrevious && <td className="pt-2 text-right tabular-nums text-fg-secondary">{formatCurrency(data.totals.previous)}</td>}
                    {data.hasLastYear && <td className="pt-2 text-right tabular-nums text-fg-secondary">{formatCurrency(data.totals.lastYear)}</td>}
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function formatBucket(start: Date, g: Granularity): string {
  if (g === 'month') return start.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
  return start.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function tooltip(b: TrendBucket, d: SalesTrend): string {
  const parts = [`This: ${b.current}`];
  if (d.hasPrevious) parts.push(`Prev: ${b.previous}`);
  if (d.hasLastYear) parts.push(`LY: ${b.lastYear}`);
  return parts.join(' · ');
}

function Bar({ value, max, className }: { value: number; max: number; className: string }) {
  const h = Math.round((Math.max(0, value) / max) * 128);
  return <div className={cn('w-2 rounded-t', className)} style={{ height: `${h}px`, minHeight: value > 0 ? 2 : 0 }} />;
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5 text-label text-fg-tertiary">
      <span className={cn('inline-block h-2.5 w-2.5 rounded-sm', color)} /> {label}
    </span>
  );
}

function KPI({ label, value, accent, delta }: { label: string; value: string; accent?: boolean; delta?: number | null }) {
  const showDelta = typeof delta === 'number';
  const up = showDelta && delta! > 0;
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={cn('mt-1 text-heading tabular-nums', accent ? 'text-accent-ink' : 'text-fg-primary')}>{value}</div>
      {showDelta && (
        <div className={cn('mt-0.5 flex items-center gap-0.5 text-label tabular-nums', up ? 'text-success-ink' : delta! < 0 ? 'text-warning-ink' : 'text-fg-tertiary')}>
          {delta !== 0 && (up ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
          {(delta! * 100).toFixed(1)}%
        </div>
      )}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="mb-2 text-label uppercase text-fg-tertiary">{title}</div>
      {children}
    </div>
  );
}

// ─── PDF export ───────────────────────────────────────────────────────────────

function buildTrendHtml(d: SalesTrend, from: Date, toExclusive: Date): string {
  const fmt = (n: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
  const fmtDate = (dt: Date) => dt.toLocaleDateString('en-IN');
  const to = lastIncludedInstant(toExclusive);
  const pctStr = (n: number) => `${(n * 100).toFixed(1)}%`;

  const rows = d.buckets.map((b) =>
    `<tr><td>${formatBucket(b.start, d.granularity)}</td>
     <td style="text-align:right">${fmt(b.current)}</td>
     ${d.hasPrevious ? `<td style="text-align:right">${fmt(b.previous)}</td>` : ''}
     ${d.hasLastYear ? `<td style="text-align:right">${fmt(b.lastYear)}</td>` : ''}</tr>`,
  ).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{font-family:sans-serif;margin:32px;color:#222}h1{font-size:22px;margin-bottom:2px}
table{width:100%;border-collapse:collapse;font-size:12px;margin-top:10px}th,td{padding:6px 8px;border-bottom:1px solid #eee}
th{text-align:right;color:#666;font-weight:500}th:first-child,td:first-child{text-align:left}
tfoot td{border-top:2px solid #333;font-weight:700}</style></head>
<body>
<h1>Sales Trend</h1>
<p style="color:#888">${fmtDate(from)} – ${fmtDate(to)} · ${d.granularity} · This ${fmt(d.totals.current)}${d.hasPrevious ? ` · vs prev ${pctStr(d.deltaPrevPct)}` : ''}${d.hasLastYear ? ` · vs LY ${pctStr(d.deltaLastYearPct)}` : ''}</p>
<table>
<thead><tr><th>Period</th><th>This period</th>${d.hasPrevious ? '<th>Previous</th>' : ''}${d.hasLastYear ? '<th>Last year</th>' : ''}</tr></thead>
<tbody>${rows}</tbody>
<tfoot><tr><td>Total</td><td style="text-align:right">${fmt(d.totals.current)}</td>${d.hasPrevious ? `<td style="text-align:right">${fmt(d.totals.previous)}</td>` : ''}${d.hasLastYear ? `<td style="text-align:right">${fmt(d.totals.lastYear)}</td>` : ''}</tr></tfoot>
</table>
<p style="margin-top:36px;font-size:11px;color:#aaa">Generated by BigxBook Desktop on ${new Date().toLocaleString('en-IN')}</p>
</body></html>`;
}
